// 내보낸 작전의 한 줄을 고치는 영역, 그 rally 행 바로 아래에 펼친다
//
// ── 왜 행 아래인가 (2026-10-14 사용자) ──────────────────────────────────────
//
// 전에는 목록 전체 아래 한 자리에 섰다. 340픽셀에서 행이 이미 두 켜라 그 안에 폼을 넣으면
// 무엇이 어느 줄의 값인지 사라진다는 판단이었다. 사용자가 8990 실화면에서 고치기를 누르자
// 편집기가 목록 끝에 생겨 대상 행과 입력칸이 멀어지는 것을 보고, 행 바로 아래에 펼치라고
// 지시했다. 앞의 걱정은 행 **안**이 아니라 행 **다음** 칸에 펼쳐 푼다. 행의 두 켜는 그대로
// 두고 편집 영역은 목록의 다음 `li` 로 선다. 행과 편집 영역은 왼쪽 강조선 하나로 이어 한
// 묶음으로 읽히게 하고(행 쪽 선은 목록이 긋는다), 상자나 배지는 늘리지 않는다(사용자).
//
// ── 무엇을 지키는가 ────────────────────────────────────────────────────────
//
// 입력 검증과 오류 문장, 불가 옵션 차단, 행군 칸이 없는 것은 옛 패널 그대로다. 옮긴 것은
// 자리뿐이다. 새로 더한 것은 넷이다(사용자 지시 항목).
//   열 때 첫 입력칸으로 초점이 간다(main 은 offset 칸이 없어 담당 roster 칸).
//   저장 성공과 취소 뒤 그 행의 고치기 버튼으로 초점이 돌아간다. 그 일은 부모가 한다.
//   저장 안 한 입력이 있는데 다른 행의 고치기를 누르면 이 영역 안에서 묻는다.
//   좁은 폭에서 아래의 다시 잡기 바가 이 영역을 가리지 않게 화면을 굴린다.
//
// **행군 칸이 없다.** 고치는 문은 명부 하나뿐이라는 것이 정본의 계약이고, 여기 칸을 하나
// 그리면 CT 는 두 자리 중 어느 것이 정본인지 묻게 된다. 담당 roster 를 바꾸면 그 줄에
// 새 사람의 지금 행군이 얼고, 같은 사람으로 두면 **얼어붙은 값이 그대로 간다**. 화면이
// 보여 주는 값도 그 규칙을 그대로 따른다.

import { useEffect, useId, useRef, useState } from 'react'

import type { Rally, Roster } from '../../../../contract/schema.ts'
import { duration } from '../../../../core/time/brand.ts'
import { formatSignedOffset } from '../../../../core/timing/format.ts'
import {
  common as commonStrings,
  type CommonStrings,
  compose as composeStrings,
  type ComposeStrings,
} from '../../../../i18n/index.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { parseTimeText, TimeInput, timeParseMessage } from '../../../ui/time-input.tsx'
import { LabelHint } from '../../../ui/label-hint.tsx'
import { RallyingOptionList } from '../rally/form-attack.tsx'
import {
  type EditBaseline,
  followServer,
  isDirty,
  type PendingMove,
  revealDelta,
} from './edit-switch.ts'
import { type LiveContext, previewOptions, type RallyEditInput } from './use-sequence.ts'

function useStrings(): ComposeStrings {
  return composeStrings(useLang().lang)
}

function useCommon(): CommonStrings {
  return commonStrings(useLang().lang)
}

function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template,
  )
}

/** 화면 위아래에 붙어 있는 두 띠 사이, 가려지지 않는 칸(화면 좌표). */
function visibleBand(): { viewTop: number; viewBottom: number } {
  const gap = 8
  const topbar = document.querySelector('[data-testid="topbar"]')
  const viewTop = (topbar === null ? 0 : topbar.getBoundingClientRect().bottom) + gap
  // 다시 잡기 바는 목록 아래에서 끈적이며 화면 바닥에 붙는다. 지금 붙어 있지 않아도 굴리면
  // 붙을 수 있으므로 늘 바의 높이만큼 바닥을 비워 둔다. 보수적으로 잡아 손해 보는 것은 몇
  // 픽셀의 덜 굴림뿐이다.
  const bar = document.querySelector('[data-testid="retime-bar"]')
  const barHeight = bar === null ? 0 : bar.getBoundingClientRect().height
  return { viewTop, viewBottom: window.innerHeight - barHeight - gap }
}

/** 상자 하나를 가려지지 않는 칸 안으로 굴린다. 이미 보이면 움직이지 않는다. */
function reveal(top: number, bottom: number): void {
  const delta = revealDelta({ top, bottom }, visibleBand())
  if (delta !== 0) window.scrollBy({ top: delta, behavior: 'instant' })
}

/** 영역 안의 첫 입력칸. offset 칸이 있으면 그것, main 이면 담당 roster 칸이다. */
function firstField(root: HTMLElement | null): HTMLElement | null {
  return root?.querySelector<HTMLElement>('input, select') ?? null
}

export interface RallyEditInlineProps {
  rally: Rally
  allies: Roster[]
  live: LiveContext
  nameOf: (rosterId: string) => string
  /** 저장. 실패하면 서버 문장, 성공하면 `null`. 성공 뒤 닫기와 초점 복귀는 부모가 한다. */
  onApply: (input: RallyEditInput) => Promise<string | null>
  /** 취소. 사람이 이 영역 안에서 고른 닫기라 묻지 않는다. */
  onCancel: () => void
  /** 저장 안 한 입력이 있는지를 부모에게 알린다. 다른 행을 누를 때 물을지를 부모가 정한다. */
  onDirtyChange: (dirty: boolean) => void
  /** 묻는 중인 이동. 없으면 `null`. */
  pending: PendingMove | null
  /** 옮겨 갈 행의 담당자 이름. 다른 행으로 옮기기를 물을 때만 쓰인다. */
  pendingName: string
  onConfirmPending: () => void
  onKeepEditing: () => void
}

export function RallyEditInline(props: RallyEditInlineProps): React.ReactElement {
  const t = useStrings()
  const c = useCommon()
  const titleId = useId()
  const askId = useId()
  const rootRef = useRef<HTMLLIElement | null>(null)
  const keepRef = useRef<HTMLButtonElement | null>(null)
  const [offsetText, setOffsetText] = useState(() =>
    formatSignedOffset(duration(props.rally.offsetMs)),
  )
  const [rallyingMs, setRallyingMs] = useState(props.rally.rallyingMs)
  const [rosterId, setRosterId] = useState(props.rally.rosterId)
  const [message, setMessage] = useState('')
  // 저장 안 한 입력을 가르는 기준. 연 순간의 서버 값으로 시작해 열린 동안 서버 값이 바뀌면 옮긴다.
  const [base, setBase] = useState<EditBaseline>(() => ({
    offsetMs: props.rally.offsetMs,
    rallyingMs: props.rally.rallyingMs,
    rosterId: props.rally.rosterId,
  }))
  /** 열린 동안 다른 곳(다른 CT 창)이 이 줄을 바꿨는가. 영역 안에 한 줄로 알린다. */
  const [changedElsewhere, setChangedElsewhere] = useState(false)

  const isMain = props.rally.type === 'main'
  const parsed = parseTimeText(offsetText)
  const offsetMs = isMain ? 0 : parsed.ok ? parsed.millis : props.rally.offsetMs

  const handedOver = rosterId !== props.rally.rosterId
  const picked = props.allies.find((row) => row.id === rosterId) ?? null
  // **같은 사람이면 얼어붙은 값을 쓴다.** 여기서 명부의 지금 값을 읽으면 화면이 보여 주는
  // 개시 여유와 서버가 실제로 쓸 값이 갈리고, 그 어긋남은 두 숫자를 손으로 빼 보기 전에는
  // 안 보인다. 서버가 같은 규칙으로 갈라 놓은 것을 화면이 그대로 따른다.
  const marchingMs = handedOver ? (picked?.marchingMs ?? null) : props.rally.marchingMs

  // `existing` 은 진행 중 문맥에서 쓰이지 않는다. 착탄 원점이 얼어붙어 새 값이 앞 줄을
  // 밀지 못하므로 「기존 줄 대비」라는 계산 자체가 없다. 빈 배열을 넘기는 것이 그 사실이다.
  const options = previewOptions({
    existing: [],
    offsetMs,
    marchingMs: marchingMs ?? 0,
    handleMs: null,
    live: props.live,
  })
  const chosen = options.find((option) => option.rallyingMs === rallyingMs) ?? null

  // ── 열린 동안 서버 값이 바뀌면(다른 창의 저장 방송) ─────────────────────────────
  // 손대지 않은 칸은 새 값을 따라가고 기준도 옮긴다. 그래서 아무것도 안 친 영역이 방송 하나로
  // 「저장 안 한 입력 있음」이 되지 않고, 옛 값을 저장해 다른 창의 변경을 덮어쓰지도 않는다.
  // 손댄 칸은 그대로 두고 영역 안에 한 줄로 알린다. 규칙은 `followServer` 가 진다.
  const serverOffsetMs = props.rally.offsetMs
  const serverRallyingMs = props.rally.rallyingMs
  const serverRosterId = props.rally.rosterId
  useEffect(() => {
    const next = { offsetMs: serverOffsetMs, rallyingMs: serverRallyingMs, rosterId: serverRosterId }
    if (
      next.offsetMs === base.offsetMs &&
      next.rallyingMs === base.rallyingMs &&
      next.rosterId === base.rosterId
    ) {
      return
    }
    const followed = followServer(base, { offsetText, rallyingMs, rosterId }, next, isMain)
    setBase(followed.base)
    setOffsetText(followed.draft.offsetText)
    setRallyingMs(followed.draft.rallyingMs)
    setRosterId(followed.draft.rosterId)
    if (followed.changed) setChangedElsewhere(true)
  }, [serverOffsetMs, serverRallyingMs, serverRosterId, base, offsetText, rallyingMs, rosterId, isMain])

  const dirty = isDirty(base, { offsetText, rallyingMs, rosterId }, isMain)
  const { onDirtyChange } = props
  useEffect(() => {
    onDirtyChange(dirty)
  }, [dirty, onDirtyChange])
  // 닫힐 때는 「저장 안 한 입력 없음」으로 돌려 둔다. 남겨 두면 다음에 여는 행이 아무것도
  // 안 쳤는데 물음부터 띄운다.
  useEffect(
    () => () => {
      onDirtyChange(false)
    },
    [onDirtyChange],
  )

  // ── 열 때: 행과 영역을 가려지지 않는 칸으로 굴리고, 첫 입력칸에 초점 ─────────────
  // 굴림을 먼저 하고 초점은 `preventScroll` 로 준다. 순서를 뒤집으면 브라우저가 입력칸 하나만
  // 보이게 굴린 뒤 다시 굴려 화면이 두 번 뛴다.
  useEffect(() => {
    const root = rootRef.current
    if (root === null) return
    const row = root.previousElementSibling
    const top = (row ?? root).getBoundingClientRect().top
    reveal(top, root.getBoundingClientRect().bottom)
    firstField(root)?.focus({ preventScroll: true })
  }, [])

  // ── 물을 때: 물음을 보이게 굴리고 「계속 편집」에 초점 ──────────────────────────
  // 다른 행을 누른 사람의 눈은 그 행에 있다. 이 영역이 화면 밖일 수 있으므로 물음을 끌어온다.
  // 초점을 안전한 쪽(입력을 지키는 쪽)에 두는 것은 세션 선택이다. Enter 한 번에 입력이
  // 사라지지 않게 하려는 것이다.
  const asking = props.pending !== null
  const wasAsking = useRef(false)
  useEffect(() => {
    if (asking) {
      const keep = keepRef.current
      const box = keep?.closest('[data-testid="rally-edit-guard"]') ?? null
      if (box !== null) {
        const rect = box.getBoundingClientRect()
        reveal(rect.top, rect.bottom)
      }
      keep?.focus({ preventScroll: true })
    } else if (wasAsking.current) {
      // 「계속 편집」으로 돌아왔다. 입력하던 자리로 초점을 돌려준다.
      firstField(rootRef.current)?.focus({ preventScroll: true })
    }
    wasAsking.current = asking
  }, [asking])

  const apply = async (): Promise<void> => {
    if (!isMain && !parsed.ok) {
      setMessage(timeParseMessage(parsed.reason, c))
      return
    }
    if (handedOver && marchingMs === null) {
      setMessage(fill(t.rosterNeedsMarchingWhy, { name: picked?.nickname ?? '' }))
      return
    }
    // **불가 옵션으로는 고쳐지지 않는다.** 서버도 막지만 화면이 먼저 말한다. 서버만
    // 막으면 CT 는 값을 다 채우고 누른 뒤에야 알게 되고, 그 한 번이 전투 중이다.
    if (chosen !== null && !chosen.feasible) {
      setMessage(t.rallyingBlocked)
      return
    }
    const failed = await props.onApply({
      rallyId: props.rally.id,
      rosterId,
      rallyingMs,
      offsetMs,
    })
    setMessage(failed ?? '')
  }

  const pendingText =
    props.pending === null
      ? ''
      : props.pending.kind === 'close'
        ? t.editUnsavedClose
        : fill(t.editUnsavedSwitch, { name: props.pendingName })

  return (
    // 목록의 자식이라 `li` 는 역할을 덮지 않는다(목록 항목으로 남는다). 묶음 역할과 이름은 안쪽
    // `div` 가 진다. `li` 에 group 을 달면 낭독기가 목록 항목 수를 행 수와 다르게 센다.
    <li
      ref={rootRef}
      data-testid="rally-edit"
      data-rally-id={props.rally.id}
      // 초점이 이 영역 안에서 키보드로 움직일 때마다(Tab 으로 저장과 취소까지 내려갈 때) 그 칸이
      // 바닥의 다시 잡기 바 밑에 깔리지 않게 굴린다. 브라우저의 기본 굴림은 끈적이는 바를 모른다.
      // 포인터로 누를 때는 굴리지 않는다. 누르는 순간(mousedown) 화면이 굴러가면 손을 뗀 자리가
      // 다른 요소가 되어 click 이 빠질 수 있다. 누른 칸은 이미 보이는 칸이다.
      onFocus={(event) => {
        if (!event.target.matches(':focus-visible')) return
        const rect = event.target.getBoundingClientRect()
        reveal(rect.top, rect.bottom)
      }}
      // 왼쪽 강조선이 바로 위 행의 같은 선과 이어져 행과 영역이 한 묶음으로 읽힌다. 상자는
      // 더하지 않는다. 색은 왼쪽 변에만 준다. 네 변의 색을 다 주면 목록이 긋는 아래 구분선까지
      // 강조색이 되어 다음 행과의 경계가 흐려진다.
      className="min-w-0 border-l-2 border-l-accent pt-line pb-group pl-3 lg:pl-3.5"
    >
      <div role="group" aria-labelledby={titleId} className="min-w-0">
      <p id={titleId} data-testid="rally-edit-target" className="text-sm font-semibold text-ink">
        {fill(t.editTitle, { name: props.nameOf(props.rally.rosterId) })}
      </p>
      <p className="mt-line text-xs text-ink-mute">{t.editWhy}</p>
      {changedElsewhere && (
        <p data-testid="rally-edit-changed" role="status" className="mt-line text-xs text-ink-dim">
          {t.editChangedElsewhere}
        </p>
      )}

      {isMain ? (
        <p className="mt-group text-xs text-ink-mute">{t.offsetMainZero}</p>
      ) : (
        <div className="mt-group">
          <TimeInput
            testId="rally-edit-offset"
            label={t.offsetLabel}
            value={offsetText}
            message={offsetText !== '' && !parsed.ok ? timeParseMessage(parsed.reason, c) : ''}
            onChange={(next) => {
              setOffsetText(next)
              setMessage('')
            }}
          />
        </div>
      )}

      <label className="mt-group flex min-w-0 flex-col gap-1 text-xs">
        <span className="font-medium text-ink-dim">{t.editRosterLabel}</span>
        <select
          data-testid="rally-edit-roster"
          className="field px-2.5 text-sm"
          value={rosterId}
          onChange={(event) => {
            setRosterId(event.target.value)
            setMessage('')
          }}
        >
          {props.allies.map((row) => (
            <option key={row.id} value={row.id}>
              {row.marchingMs === null
                ? `${row.nickname} (${t.rosterNeedsMarching})`
                : row.nickname}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="mt-group min-w-0">
        {/* 범례는 라벨과 뜻풀이 두 요소(문구 정리, 2026-10-14 사용자 지시). 구성 폼의 같은 범례와 같은 모양이다. */}
        <legend className="text-xs font-medium text-ink-dim">
          <LabelHint label={t.rallyingLabel} hint={t.rallyingLabelHint} hintClassName="font-normal text-ink-mute" />
        </legend>
        <p className="mt-line text-xs text-ink-mute">{t.rallyingNoShift}</p>
        <RallyingOptionList
          options={options}
          selected={rallyingMs}
          testId="rally-edit-options"
          // main 은 착탄 간격이 정의상 0 이라 「간격을 이만큼으로」가 시킬 수 없는 일이다.
          showMinimum={!isMain}
          onSelect={(next) => {
            setRallyingMs(next)
            setMessage('')
          }}
        />
      </fieldset>

      {/* 거절 문장과 버튼 줄은 한 묶음으로 다시 잡기 바 바로 위에 끈적인다. 편집 영역이 화면보다
          길어도(360 에서 약 700픽셀) 저장과 취소가 바 밑에 깔리지 않는다. 바 높이는 바를 그리는
          쪽이 재서 변수로 내놓는다. 면은 목록이 놓인 카드 면과 같은 불투명 색이라 뒤의 입력칸이
          비치지 않는다(2026-10-14 사용자 지시 「retime 패널이 가리지 않게」, 방식은 세션 선택).
          거절 문장은 버튼 바로 위에 선다. 버튼 아래에 두면 누른 사람이 이유를 못 본다. */}
      <div
        data-testid="rally-edit-actions"
        className="sticky bottom-[var(--retime-bar-h,0px)] z-[3] mt-group bg-surface-raised pt-line pb-line"
      >
      {message !== '' && (
        <p data-testid="rally-edit-message" role="alert" className="mb-line text-sm text-red-300">
          {message}
        </p>
      )}

      {asking ? (
        // ── 저장 안 한 입력을 지키는 물음 ────────────────────────────────────────
        // 저장과 취소 자리를 이 물음이 잠시 대신한다. 둘을 함께 두면 버튼 넷이 한 줄에 서서
        // 무엇에 답하는 버튼인지 흐려진다.
        <div data-testid="rally-edit-guard">
          {/* 두 문장을 한 알림으로 묶는다. 「변경이 있다」만 읽고 어느 줄로 가려 했는지를 빠뜨리면
              낭독기 사용자는 무엇을 버리는지 모른 채 고른다. 초점을 받는 버튼도 같은 문장을 설명으로 든다. */}
          <div id={askId} role="alert">
            <p className="text-sm font-semibold text-ink">{t.editUnsaved}</p>
            <p className="mt-line text-xs text-ink-dim">{pendingText}</p>
          </div>
          <div className="mt-line flex flex-wrap gap-2">
            <button
              ref={keepRef}
              type="button"
              data-testid="rally-edit-keep"
              aria-describedby={askId}
              className="btn btn-secondary flex-1"
              onClick={props.onKeepEditing}
            >
              {t.editKeepEditing}
            </button>
            <button
              type="button"
              data-testid="rally-edit-discard"
              className="btn btn-ghost"
              onClick={props.onConfirmPending}
            >
              {t.editDiscard}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="rally-edit-apply"
            className="btn btn-secondary flex-1"
            onClick={() => void apply()}
          >
            {t.editApply}
          </button>
          <button
            type="button"
            data-testid="rally-edit-close"
            className="btn btn-ghost"
            onClick={props.onCancel}
          >
            {t.editRallyClose}
          </button>
        </div>
      )}
      </div>
      </div>
    </li>
  )
}
